import React, { useState, useEffect, useCallback } from "react";
import style from "../BulkUploadTabs.module.css";
import Button from "../../../component/button/Button";
import { FaCloudArrowUp } from "react-icons/fa6";
import { FiVideo, FiImage, FiRefreshCw, FiPlus, FiCheckCircle } from "react-icons/fi";
import { showToast } from "../../../utils/Toastify/Toastify";
import {
  bunnyApi,
  getBunnyConnections,
  syncBunnyConnection,
} from "../../../api/bulk-upload-api";
import InputText from "../../../component/inputText/InputText";

// A labelled input with a short helper hint underneath.
const Field = ({ label, hint, optional, required, children }) => (
  <div className={style.bunnyField}>
    <label className={style.bunnyLabel}>
      {label}
      {required && <span style={{ color: "var(--app-primary)" }}> *</span>}
      {/* {optional && <span className={style.bunnyOptional}>optional</span>} */}
    </label>
    {children}
    {hint && <p className={style.bunnyHint}>{hint}</p>}
  </div>
);

const Bunny = () => {
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  // When true, show the credentials form (initial connect, or "Add another").
  const [showForm, setShowForm] = useState(false);
  const [credentials, setCredentials] = useState({
    stream_api_key: "",
    library_id: "",
    pull_zone: "",
    // Images come from Bunny Storage (videos come from Stream). Optional.
    storage_zone_name: "",
    storage_password: "",
  });

  // Load saved connections on mount. These carry NO secrets — the API key and
  // password stay server-side; we only get library id / zone name / flags.
  const loadConnections = useCallback(async () => {
    try {
      const res = await getBunnyConnections();
      setConnections(res?.data || []);
    } catch (error) {
      // Non-fatal: just fall back to the connect form.
      setConnections([]);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const update = (key) => (e) =>
    setCredentials((prev) => ({ ...prev, [key]: e.target.value }));

  const resetForm = () =>
    setCredentials({
      stream_api_key: "",
      library_id: "",
      pull_zone: "",
      storage_zone_name: "",
      storage_password: "",
    });

  // One-click re-sync of a saved connection (no re-typing; secrets stay on server).
  const handleSyncNow = async (id) => {
    setSyncingId(id);
    try {
      await syncBunnyConnection(id);
      showToast("Syncing… new files will appear in the Media Library shortly.", "success");
    } catch (error) {
      showToast("Sync failed. Please try again.", "error");
    } finally {
      setSyncingId(null);
    }
  };

  const handleConnectClick = async () => {
    const libraryId = credentials.library_id.trim();
    if (!credentials.stream_api_key.trim()) {
      showToast("Stream API Key is required", "error");
      return;
    }
    if (libraryId === "" || !/^\d+$/.test(libraryId)) {
      showToast("Library ID must be a valid number", "error");
      return;
    }
    // For images, both storage fields are needed (or neither).
    const hasZone = !!credentials.storage_zone_name.trim();
    const hasPass = !!credentials.storage_password.trim();
    if (hasZone !== hasPass) {
      showToast(
        "For images, enter both the Storage Zone Name and Password (or leave both empty).",
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      await bunnyApi({ ...credentials, library_id: libraryId });
      showToast("Connected! Importing your Bunny content…", "success");
      resetForm();
      setShowForm(false);
      loadConnections(); // refresh the saved-connections list
    } catch (error) {
      showToast("Failed to connect. Please check your credentials.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Show the saved-connections view when we have at least one and the user
  // isn't explicitly adding a new one.
  const showSavedView = !loadingConnections && connections.length > 0 && !showForm;

  return (
    <div className={style.cloudStorage}>
      <h3>Bunny Integration</h3>
      <p>Import your existing videos and images from Bunny.net.</p>

      {showSavedView ? (
        <div className={`${style.connectBox} ${style.bunnyConnectBox}`}>
          <div className={style.bunnySavedHeader}>
            <FiCheckCircle className={style.bunnySavedIcon} /> Connected accounts
          </div>

          {connections.map((c) => (
            <div key={c.id} className={style.bunnySavedCard}>
              <div className={style.bunnySavedInfo}>
                <div className={style.bunnySavedTitle}>
                  <FiVideo /> Stream Library{" "}
                  <span className={style.bunnySavedId}>#{c.library_id}</span>
                </div>
                <div className={style.bunnySavedMeta}>
                  {c.has_storage ? (
                    <span className={style.bunnySavedTag}>
                      <FiImage /> Images: {c.storage_zone_name}
                    </span>
                  ) : (
                    <span className={style.bunnySavedTagMuted}>Videos only</span>
                  )}
                </div>
              </div>
              <Button
                className={style.connectButton}
                onClick={() => handleSyncNow(c.id)}
                disabled={syncingId === c.id}
              >
                <FiRefreshCw
                  className={syncingId === c.id ? style.bunnySpin : undefined}
                />{" "}
                {syncingId === c.id ? "Syncing…" : "Sync Now"}
              </Button>
            </div>
          ))}

          <button
            type="button"
            className={style.bunnyAddAnother}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <FiPlus /> Connect a different Bunny account
          </button>

          <p className={style.bunnyReconnectNote}>
            Click <b>Sync Now</b> anytime to pull new files you've added to Bunny —
            already-imported files are skipped (no duplicates). No need to re-enter
            your credentials.
          </p>
        </div>
      ) : (
      <div className={`${style.connectBox} ${style.bunnyConnectBox}`}>
        <FaCloudArrowUp className={style.cloudIcon} />

        {/* Videos — Bunny Stream */}
        <div className={style.bunnySectionHeader}>
          <FiVideo /> Videos
        </div>
        <div className={style.awsForm}>
          <Field label="Stream API Key" hint="Bunny → Stream → API" required>
            <InputText
              className={style.awsFormInput}
              type="password"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={credentials.stream_api_key}
              onChange={update("stream_api_key")}
            />
          </Field>
          <Field label="Library ID" hint="Numeric ID of your Stream library" required>
            <InputText
              className={style.awsFormInput}
              type="text"
              placeholder="123456"
              value={credentials.library_id}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) update("library_id")(e);
              }}
            />
          </Field>
        </div>

        {/* Images — Bunny Storage (optional) */}
        <div className={style.bunnySectionHeader}>
          <FiImage /> Images{" "}
          {/* <span className={style.bunnyBadgeOpt}>optional</span> */}
        </div>
        <div className={style.awsForm}>
          <Field label="Storage Zone Name" hint="The zone name, not a URL" required>
            <InputText
              className={style.awsFormInput}
              type="text"
              placeholder="my-storage-zone"
              value={credentials.storage_zone_name}
              onChange={update("storage_zone_name")}
            />
          </Field>
          <Field
            label="Storage Password"
            hint="Bunny → Storage → Access (use read-only)"
            required
          >
            <InputText
              className={style.awsFormInput}
              type="password"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={credentials.storage_password}
              onChange={update("storage_password")}
            />
          </Field>
          <Field label="Pull Zone URL" hint="Public CDN URL for your files" required>
            <InputText
              className={style.awsFormInput}
              type="text"
              placeholder="https://my-zone.b-cdn.net"
              value={credentials.pull_zone}
              onChange={update("pull_zone")}
            />
          </Field>
        </div>

        <div className={style.connectButtonContainer}>
          <Button
            className={style.connectButton}
            onClick={handleConnectClick}
            disabled={loading}
          >
            {loading ? "Connecting…" : "🔗 Connect & Import"}
          </Button>
        </div>

        {connections.length > 0 && (
          <button
            type="button"
            className={style.bunnyAddAnother}
            onClick={() => setShowForm(false)}
          >
            ← Back to connected accounts
          </button>
        )}

        <p className={style.bunnyReconnectNote}>
          
          Your credentials are saved securely — next time just click{" "}
          <b>Sync Now</b>. The API key and password are never shown again.
        </p>
      </div>
      )}
    </div>
  );
};

export default Bunny;
